import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import {
  BookOpen, Code2, Terminal, Copy, ShieldCheck, Zap, Globe,
  AlertTriangle, Check, Server, ChevronRight,
} from 'lucide-react';
import { Card } from '../components/Card';
import { SectionHeader } from '../components/Tabs';
import { Badge } from '../components/Badge';
import { Button } from '../components/Button';
import { Tabs } from '../components/Tabs';
import { copyToClipboard } from '../lib/utils';

/**
 * DocsPage — wrapper that redirects to the new public /docs page.
 *
 * The documentation now lives at /docs (no login required) and is rendered
 * by PublicDocsPage with a richer Firecrawl-style layout. This wrapper exists
 * so the in-app nav link still works without us having to update every
 * navigation reference.
 */
export default function DocsPage({ apiKey }) {
  return <Navigate to="/docs" replace />;
}

/* The implementation below is preserved (unused) as a reference for any
   future in-app specific docs we may want to add. Left intact to keep the
   diff small. */
function _LegacyDocsPage({ apiKey }) {
  const [tab, setTab] = useState('quickstart');
  return (
    <div className="space-y-8 max-w-4xl">
      <SectionHeader
        title="Documentation"
        description="Everything you need to integrate Datames into your app."
      />

      <Tabs
        value={tab}
        onChange={setTab}
        items={[
          { value: 'quickstart', label: 'Quickstart', icon: Zap },
          { value: 'api', label: 'API reference', icon: Code2 },
          { value: 'sdks', label: 'SDKs', icon: Server },
          { value: 'errors', label: 'Error codes', icon: AlertTriangle },
        ]}
      />

      {tab === 'quickstart' && <Quickstart key_={apiKey} />}
      {tab === 'api' && <ApiReference />}
      {tab === 'sdks' && <Sdks />}
      {tab === 'errors' && <ErrorCodes />}
    </div>
  );
}

function Quickstart({ key_ }) {
  return (
    <div className="space-y-6">
      <Card>
        <Card.Header>
          <div>
            <Card.Title>1. Get an API key</Card.Title>
            <Card.Subtitle>You need a key to authenticate every request.</Card.Subtitle>
          </div>
        </Card.Header>
        <Card.Body className="text-sm text-fg-muted leading-relaxed">
          Go to <strong className="text-fg">API Keys</strong> in the sidebar and click <code className="font-mono text-fg bg-surface-2 px-1.5 py-0.5 rounded">Create key</code>.
          Save the plaintext — you won't see it again.
        </Card.Body>
      </Card>

      <Card>
        <Card.Header>
          <div>
            <Card.Title>2. Make your first request</Card.Title>
            <Card.Subtitle>POST a URL to <code className="font-mono">/v1/fetch</code> and get back the rendered HTML.</Card.Subtitle>
          </div>
        </Card.Header>
        <Card.Body>
          <CodeBlock language="bash" code={`curl -X POST https://api.datames.dev/v1/fetch \\
  -H "x-api-key: ${key_ || 'sk_your_key_here'}" \\
  -H "Content-Type: application/json" \\
  -d '{"url": "https://example.com"}'`} />
          <ResponsePreview body={`{
  "success": true,
  "engine": "tls_client_fast",
  "url": "https://example.com/",
  "status": 200,
  "challenge": false,
  "body": "<html>...</html>"
}`} />
        </Card.Body>
      </Card>

      <Card>
        <Card.Header>
          <div>
            <Card.Title>3. Behind Cloudflare? We've got you.</Card.Title>
            <Card.Subtitle>CF sites are solved automatically — no extra config.</Card.Subtitle>
          </div>
        </Card.Header>
        <Card.Body className="text-sm text-fg-muted leading-relaxed space-y-3">
          <p>
            The first request to a Cloudflare-protected domain takes ~2 seconds while we solve the challenge and cache a <code className="font-mono text-fg bg-surface-2 px-1.5 py-0.5 rounded">cf_clearance</code> cookie.
          </p>
          <p>
            Every subsequent request within 30 minutes uses the cached cookie. No code changes needed — same endpoint, same JSON.
          </p>
          <div className="flex items-start gap-2 p-3 rounded-md bg-info/10 border border-info/30 text-info">
            <Globe className="h-3.5 w-3.5 shrink-0 mt-0.5" />
            <span className="text-xs">Try <code className="font-mono">https://nowsecure.nl</code> — it's behind real Cloudflare and solved in &lt;2s.</span>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
}

function ApiReference() {
  return (
    <div className="space-y-6">
      <Endpoint
        method="POST"
        path="/v1/fetch"
        description="Scrape a URL. Returns the rendered HTML, status code, headers, and which engine was used."
        params={[
          { name: 'url', type: 'string', required: true, desc: 'The URL to fetch. http or https.' },
          { name: 'response_type', type: '"html" | "markdown" | "text" | "screenshot"', desc: 'Default "html".' },
          { name: 'proxy_mode', type: '"stealth" | "classic" | null', desc: 'Default null (dynamic). Use "stealth" to force Datames. "classic" uses the operator’s residential proxy.' },
          { name: 'allowed_status_codes', type: 'number[]', desc: 'HTTP codes considered "success". Default [200, 201, 202, 203, 204, 206].' },
          { name: 'include_screenshot', type: 'boolean', desc: 'Attach a base64 PNG screenshot.' },
        ]}
      />
      <Endpoint
        method="GET"
        path="/v1/keys"
        description="List your API keys."
      />
      <Endpoint
        method="POST"
        path="/v1/keys?label=..."
        description="Create a new API key. Returns the plaintext once."
      />
      <Endpoint
        method="DELETE"
        path="/v1/keys/{id}"
        description="Revoke a key. Idempotent."
      />
      <Endpoint
        method="GET"
        path="/payments/plans"
        description="List available plans (free, starter, pro, enterprise)."
      />
      <Endpoint
        method="POST"
        path="/payments/checkout/subscription?plan_code=pro"
        description="Start a crypto checkout for a subscription. Returns a NOWPayments URL."
      />
      <Endpoint
        method="POST"
        path="/payments/checkout/topup?usd_cents=1000"
        description="Buy credits without a subscription. Returns a checkout URL."
      />
      <Endpoint
        method="GET"
        path="/payments/usage/daily?days=30"
        description="Daily usage aggregates. Powers the charts on your dashboard."
      />
    </div>
  );
}

function Endpoint({ method, path, description, params = [] }) {
  const methodColor = {
    GET: 'success',
    POST: 'info',
    PUT: 'warning',
    DELETE: 'destructive',
  }[method];
  return (
    <Card>
      <Card.Header>
        <div className="flex items-center gap-3 flex-1">
          <Badge variant={methodColor}>{method}</Badge>
          <code className="font-mono text-sm text-fg">{path}</code>
        </div>
      </Card.Header>
      <Card.Body className="space-y-4">
        {description && <p className="text-sm text-fg-muted">{description}</p>}
        {params.length > 0 && (
          <div className="rounded-md border border-border overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-surface-2/50">
                <tr className="text-2xs font-medium text-fg-muted uppercase tracking-wider">
                  <th className="text-left px-3 py-2">Param</th>
                  <th className="text-left px-3 py-2">Type</th>
                  <th className="text-left px-3 py-2">Description</th>
                </tr>
              </thead>
              <tbody>
                {params.map((p) => (
                  <tr key={p.name} className="border-t border-border">
                    <td className="px-3 py-2 font-mono text-fg">
                      {p.name}{p.required && <span className="text-destructive ml-0.5">*</span>}
                    </td>
                    <td className="px-3 py-2 font-mono text-fg-muted">{p.type}</td>
                    <td className="px-3 py-2 text-fg-muted">{p.desc}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card.Body>
    </Card>
  );
}

function Sdks() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <SdkCard
        name="Python"
        package="pip install datames"
        status="ready"
        code={`from datames import Client
client = Client(api_key="sk_...")
page = client.fetch("https://example.com")
print(page.status, page.body[:200])`}
      />
      <SdkCard
        name="Node.js"
        package="npm install datames"
        status="ready"
        code={`import { Client } from "datames";
const client = new Client({ apiKey: "sk_..." });
const page = await client.fetch("https://example.com");
console.log(page.status, page.body.slice(0, 200));`}
      />
      <SdkCard
        name="Go"
        package="go get github.com/datames/datames-go"
        status="ready"
        code={`client := datames.NewClient("sk_...")
page, _ := client.Fetch(ctx, "https://example.com")
fmt.Println(page.Status, page.Body[:200])`}
      />
      <SdkCard
        name="cURL / Raw HTTP"
        package="any HTTP client"
        status="ready"
        code={`curl -X POST https://api.datames.dev/v1/fetch \\
  -H "x-api-key: sk_..." \\
  -H "Content-Type: application/json" \\
  -d '{"url":"https://example.com"}'`}
      />
    </div>
  );
}

function SdkCard({ name, package: pkg, status, code }) {
  return (
    <Card>
      <Card.Header>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <Card.Title>{name}</Card.Title>
            <Badge variant="success"><Check className="h-3 w-3" /> {status}</Badge>
          </div>
          <code className="text-2xs text-fg-muted font-mono mt-1 block">{pkg}</code>
        </div>
      </Card.Header>
      <Card.Body className="pt-0">
        <CodeBlock language={name.toLowerCase()} code={code} />
      </Card.Body>
    </Card>
  );
}

function ErrorCodes() {
  const codes = [
    { code: 400, name: 'Bad Request', desc: 'Malformed JSON or missing required field.' },
    { code: 401, name: 'Unauthorized', desc: 'Missing or invalid x-api-key header.' },
    { code: 402, name: 'Payment Required', desc: 'No credits remaining. Top up or upgrade.' },
    { code: 429, name: 'Too Many Requests', desc: 'Rate limit per minute exceeded (per-plan limit).' },
    { code: 500, name: 'Internal Server Error', desc: 'Something broke on our end. Retry with exponential backoff.' },
    { code: 502, name: 'Bad Gateway', desc: 'Upstream (Datames or target site) returned a non-2xx status.' },
    { code: 504, name: 'Gateway Timeout', desc: 'Scrape took longer than the configured timeout.' },
  ];
  return (
    <Card>
      <Card.Body className="p-0">
        <table className="w-full text-sm">
          <thead className="border-b border-border bg-surface-2/30">
            <tr className="text-2xs font-medium text-fg-muted uppercase tracking-wider">
              <th className="text-left px-5 py-3">Status</th>
              <th className="text-left px-5 py-3">Name</th>
              <th className="text-left px-5 py-3">Meaning</th>
            </tr>
          </thead>
          <tbody>
            {codes.map((c) => (
              <tr key={c.code} className="border-b border-border last:border-b-0">
                <td className="px-5 py-3"><code className="font-mono text-xs">{c.code}</code></td>
                <td className="px-5 py-3 text-fg">{c.name}</td>
                <td className="px-5 py-3 text-fg-muted">{c.desc}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card.Body>
    </Card>
  );
}

function CodeBlock({ language, code }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    if (await copyToClipboard(code)) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  }
  return (
    <div className="relative group">
      <pre className="rounded-md bg-bg border border-border p-4 overflow-x-auto text-xs font-mono text-fg leading-relaxed">
        <code>{code}</code>
      </pre>
      <button
        onClick={copy}
        className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-1 text-2xs font-medium rounded-md bg-surface-2 border border-border text-fg-muted hover:text-fg opacity-0 group-hover:opacity-100 transition-opacity"
      >
        {copied ? <><Check className="h-3 w-3 text-success" /> Copied</> : <><Copy className="h-3 w-3" /> Copy</>}
      </button>
    </div>
  );
}

function ResponsePreview({ body }) {
  return (
    <pre className="mt-3 rounded-md bg-surface-2/40 border border-border p-4 overflow-x-auto text-xs font-mono text-fg-muted leading-relaxed">
      <code>{body}</code>
    </pre>
  );
}